import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { Subject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { OfferSetListService } from '../../../../service/offer-set-list.service';
import { OfferSetListItem } from '../../../../model/offer-set-list-item.model';
import { OfferSetListPagination } from '../../../../model/offer-set-list-response.model';
import { OfferSetBulkActionRequest } from '../../../../model/offer-set-bulk-action.model';
import { environment } from '../../../../../environments/environment';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { OfferSetRegistryService } from '../../../../service/offer-set-registry-service';
import {OfferSetPublicOffersDto, OfferSetPublicOffersService} from '../../../../service/offer-set-public-offers.service';
import {UserService} from '../../../../service/user.service';
import {VehicleService} from '../../../../service/vehicle.service';
import {MakeModel} from '../../../../model/make.model';
import {VehicleModel} from '../../../../model/vehicle-model.model';
import {OfferSetCreateRequest} from '../../../../model/offer-set-create.model';
import {CreateAdFromStockRequest, CreateAdFromStockResponse} from '../../../../model/create-ad-from-stock.model';
import {Menu} from 'primeng/menu';

interface PresetTab {
  label: string;
  preset: string;
  status: string;
  condition: string;
  visibility: string;
}

interface FilterOption {
  label: string;
  value: string;
}

@Component({
  selector: 'auto-offer-set-list',
  templateUrl: './offer-set-list.component.html',
  styleUrls: ['./offer-set-list.component.scss'],
  standalone: false
})
export class OfferSetListComponent implements OnInit, OnDestroy {
  accountId: number;
  items: OfferSetListItem[] = [];
  pagination: OfferSetListPagination = {
    page: 1,
    pageSize: 50,
    totalItems: 0,
    totalPages: 0
  };
  isLoading = false;
  selectedItems: OfferSetListItem[] = [];
  allSelected = false;
  selectAllAcrossPages = false;
  searchTerm = '';
  activePreset = 'new_active';
  selectedBulkAction = '';
  selectedStatus = 'active';
  selectedCondition = 'new';
  selectedVisibility = 'full_visibility';
  enableNewOfferSetCreationModal = false;
  inventoryMenuItems: MenuItem[] = [
    {
      label: 'New Inventory',
      command: () => this.viewInventory('new')
    },
    {
      label: 'Used Inventory',
      command: () => this.viewInventory('used')
    }
  ];

  // Disclosure dialog state
  disclosureDialogVisible = false;
  disclosureTitle = '';
  disclosureHtml: SafeHtml | string = '';

  // Build New Offer Set dialog state
  private readonly conditionNew = 'New';
  private readonly conditionLoaner = 'Loaner';
  private readonly stockConditionNew = 'new';
  private readonly stockConditionUsed = 'used';
  private readonly mismatchRedirectDelayMs = 2500;
  private readonly yearOptionsFutureOffset = 4;
  private readonly newVehicleStartYear = 2014;
  private readonly usedVehicleStartYear = 2000;
  buildDialogVisible = false;
  manualDialogVisible = false;
  stockDialogVisible = false;
  isCreatingFromStock = false;
  makes: MakeModel[] = [];
  models: VehicleModel[] = [];
  filteredModels: VehicleModel[] = [];
  yearOptions: number[] = this.buildYearOptions('new');
  newOfferSetCondition = this.conditionNew;
  newOfferSetYear: number | null = null;
  newOfferSetMakeId: number | null = null;
  newOfferSetModelId: number | null = null;
  newOfferSetTrim = '';
  newOfferSetStockNumber = '';
  buildConditionOptions: FilterOption[] = [
    {label: this.conditionNew, value: this.conditionNew},
    {label: this.conditionLoaner, value: this.conditionLoaner}
  ];

  publicOffersByOfferSetId: Record<number, OfferSetPublicOffersDto> = {};

  presetTabs: PresetTab[] = [
    { label: 'New · Active', preset: 'new_active', status: 'active', condition: 'new', visibility: 'full_visibility' },
    { label: 'New · Inactive', preset: 'new_inactive', status: 'inactive', condition: 'new', visibility: 'full_visibility' },
    { label: 'Used · Active', preset: 'used_active', status: 'active', condition: 'used', visibility: 'full_visibility' },
    { label: 'Used · Inactive', preset: 'used_inactive', status: 'inactive', condition: 'used', visibility: 'full_visibility' },
    { label: 'All Offer Sets', preset: 'all', status: 'any', condition: 'any', visibility: 'any' }
  ];

  statusOptions: FilterOption[] = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Any', value: 'any' }
  ];

  conditionOptions: FilterOption[] = [
    { label: 'New', value: 'new' },
    { label: 'Used', value: 'used' },
    { label: 'Any', value: 'any' }
  ];

  visibilityOptions: FilterOption[] = [
    { label: 'Full Visibility', value: 'full_visibility' },
    { label: 'Inventory Only', value: 'inventory_only' },
    { label: 'Any', value: 'any' }
  ];

  bulkActionOptions: FilterOption[] = [
    { label: 'Bulk Action: Select', value: '' },
    { label: 'Activate', value: 'activate' },
    { label: 'Inactivate', value: 'inactivate' },
    { label: 'Delete', value: 'delete' }
  ];

  pageSizeOptions: FilterOption[] = [
    { label: '25', value: '25' },
    { label: '50', value: '50' },
    { label: '100', value: '100' }
  ];

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  private isSelectable(item: OfferSetListItem): boolean {
    return !item.isShared;
  }

  private getSelectableItems(items: OfferSetListItem[]): OfferSetListItem[] {
    return (items || []).filter((item) => this.isSelectable(item));
  }

  getSelectableItemCount(): number {
    const selectableItems = this.getSelectableItems(this.items);

    return selectableItems.length;
  }

  areAllSelectableItemsSelected(): boolean {
    const selectableCount = this.getSelectableItemCount();
    const allSelected = selectableCount > 0 && this.selectedItems.length === selectableCount;

    return allSelected;
  }

  constructor(
    private route: ActivatedRoute,
    private offerSetListService: OfferSetListService,
    private offerRegistryService: OfferSetRegistryService,
    private offerSetPublicOffersService: OfferSetPublicOffersService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private sanitizer: DomSanitizer,
    private userService: UserService,
    private vehicleService: VehicleService
  ) {}

  ngOnInit(): void {
    this.accountId = Number(this.route.snapshot.paramMap.get('accountId'));
    this.initSearchDebounce();
    this.loadOfferSets();
    this.loadNewOfferSetCreationModalFeatureFlag();
  }

  /**
   * Loads the enableNewOfferSetCreationModal MPOP feature flag, defaulting to false on error.
   */
  private loadNewOfferSetCreationModalFeatureFlag(): void {
    const firstElement = 0;
    this.userService.getMpopFeature('enableNewOfferSetCreationModal').pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (feature) => {
        this.enableNewOfferSetCreationModal = feature[firstElement]?.enabled ?? false;
      },
      () => {
        this.enableNewOfferSetCreationModal = false;
      }
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initializes debounced search subscription
   */
  initSearchDebounce(): void {
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((term: string) => {
      this.searchTerm = term;
      this.pagination.page = 1;
      this.loadOfferSets();
    });
  }

  /**
   * Handles search input changes
   */
  onSearchChange(term: string): void {
    this.searchSubject.next(term);
  }

  /**
   * Selects a preset tab and loads data
   */
  selectPreset(tab: PresetTab): void {
    this.activePreset = tab.preset;
    this.selectedStatus = tab.status;
    this.selectedCondition = tab.condition;
    this.selectedVisibility = tab.visibility;
    this.pagination.page = 1;
    this.clearSelection();
    this.loadOfferSets();
  }

  /**
   * Handles filter dropdown changes
   */
  onFilterChange(): void {
    this.activePreset = '';
    this.pagination.page = 1;
    this.clearSelection();
    this.loadOfferSets();
  }

  /**
   * Handles pagination page change
   */
  onPageChange(event: any): void {
    this.pagination.page = (event.page + 1);
    this.pagination.pageSize = event.rows;
    this.clearSelection();
    this.loadOfferSets();
  }

  /**
   * Loads offer sets from the API
   */
  loadOfferSets(): void {
    this.isLoading = true;
    this.offerSetListService.getOfferSetList(
      this.accountId,
      this.activePreset,
      this.selectedStatus,
      this.selectedCondition,
      this.selectedVisibility,
      this.searchTerm,
      this.pagination.page,
      this.pagination.pageSize
    ).subscribe(
      (response) => {
        this.items = response.items.map((item) => ({
          ...item,
          isMissingMakeOrModel: this.computeIsMissingMakeOrModel(item)
        }));
        this.pagination = response.pagination;
        this.isLoading = false;

        this.publicOffersByOfferSetId = {};
        const offerSetIds = this.items
          .map((item) => Number(item.id))
          .filter((id) => Number.isFinite(id) && id > 0);

        if (offerSetIds.length > 0) {
          this.offerSetPublicOffersService.getPublicOffersByOfferSetIds(offerSetIds).subscribe(
            (publicOffers) => {
              this.publicOffersByOfferSetId = this.buildPublicOffersLookup(publicOffers);
            },
            () => {
              this.publicOffersByOfferSetId = {};
            }
          );
        }

        this.offerRegistryService.callOfferRegistryService(this.accountId).subscribe(
          () => {
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to sync Offer Sets.',
              life: 5000
            });
          }
        );
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Offer Sets.',
          life: 5000
        });
        this.isLoading = false;
      }
    );
  }

  /**
   * Build a lookup of public offers by offer set ID
   */
  private buildPublicOffersLookup(publicOffers: OfferSetPublicOffersDto[]): Record<number, OfferSetPublicOffersDto> {
    const lookup: Record<number, OfferSetPublicOffersDto> = {};

    publicOffers.forEach((dto) => {
      const standardOfferSetId = dto.standardOfferSetId ?? null;
      if (standardOfferSetId !== null && standardOfferSetId > 0) {
        lookup[standardOfferSetId] = dto;
      }
    });

    return lookup;
  }

  /**
   * Checks if the offer sets has public rules associated with it to mark with Public tag
   */
  hasActivePublicOffer(itemId: number): boolean {
    const publicOffers = this.publicOffersByOfferSetId[itemId] ?? null;
    let hasActiveOffer = false;

    if (publicOffers !== null) {
      hasActiveOffer = publicOffers.leaseOfferIsPublic
        || publicOffers.alternateLeaseIsPublic
        || publicOffers.aprOfferIsPublic
        || publicOffers.financeOfferIsPublic;
    }

    return hasActiveOffer;
  }

  /**
   * Toggles selection of all visible items
   */
  toggleSelectAll(event: { checked: boolean }): void {
    this.selectAllAcrossPages = false;

    const selectableItems = this.getSelectableItems(this.items);
    this.allSelected = event.checked;

    if (this.allSelected) {
      this.selectedItems = [...selectableItems];
    } else {
      this.selectedItems = [];
    }
  }

  /**
   * Clears all selections
   */
  clearSelection(): void {
    this.selectedItems = [];
    this.allSelected = false;
    this.selectAllAcrossPages = false;
  }

  /**
   * Checks if a specific item is selected
   */
  isItemSelected(item: OfferSetListItem): boolean {
    return this.selectedItems.some(selected => selected.id === item.id);
  }

  /**
   * Toggles selection of a single item
   */
  toggleItemSelection(item: OfferSetListItem): void {
    this.selectAllAcrossPages = false;

    if (!this.isSelectable(item)) {
      return;
    }

    if (this.isItemSelected(item)) {
      this.selectedItems = this.selectedItems.filter(selected => selected.id !== item.id);
    } else {
      this.selectedItems = [...this.selectedItems, item];
    }

    const selectableItems = this.getSelectableItems(this.items);
    this.allSelected = selectableItems.length > 0 && this.selectedItems.length === selectableItems.length;
  }

  /**
   * Marks selection as all results across pages
   */
  selectAllAcross(): void {
    this.selectAllAcrossPages = true;
    this.allSelected = true;

    const selectableItems = this.getSelectableItems(this.items);
    this.selectedItems = [...selectableItems];
  }

  /**
   * Returns true when the offer set is missing a make or model, blocking activation.
   */
  private computeIsMissingMakeOrModel(item: OfferSetListItem): boolean {
    const makeId = Number(item.data.makeId);
    const modelId = Number(item.data.modelId);
    const makeIsMissing = !makeId || makeId <= 0;
    const modelIsMissing = !modelId || modelId <= 0;

    return makeIsMissing || modelIsMissing;
  }

  /**
   * Toggles the active status of a single offer set
   */
  toggleStatus(item: OfferSetListItem): void {
    const newAction = item.data.active ? 'inactivate' : 'activate';
    const request: OfferSetBulkActionRequest = {
      accountId: this.accountId,
      action: newAction,
      offerSetIds: [item.id]
    };
    this.offerSetListService.executeBulkAction(request).subscribe(
      () => {
        this.loadOfferSets();
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update Offer Set status.',
          life: 5000
        });
      }
    );
  }

  /**
   * Navigates to the edit page for an offer set
   */
  editOfferSet(item: OfferSetListItem): void {
    const adminDomain = environment.launchcontrolAdminDomain;
    const accountId = this.accountId;
    const offerSetId = item?.id;
    const condition = this.resolveEditorCondition(item.data.newUsed);

    if (!adminDomain || !accountId || !offerSetId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Navigation Error',
        detail: 'Missing data required to open the editor.',
        life: 4000
      });
    } else {
      const url = `http://${adminDomain}/${condition}-specials/${accountId}#/specials/edit/${offerSetId}`;

      window.location.href = url;
    }
  }

  /**
   * Navigates to the edit page for a matched (public-source) offer set.
   */
  openMatchedOfferSetEditor(matchedOfferSetId: number | null | undefined): void {
    const adminDomain = environment.launchcontrolAdminDomain;
    const accountId = this.accountId;
    const offerSetId = matchedOfferSetId ?? null;

    if (!adminDomain || !accountId || !offerSetId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Navigation Error',
        detail: 'Missing data required to open the editor.',
        life: 4000
      });

      return;
    }

    const url = `http://${adminDomain}/new-specials/${accountId}#/specials/edit/${offerSetId}`;

    window.location.href = url;
  }

  /**
   * Opens the disclosure dialog with the item's fullDisclosure content
   */
  openDisclosure(item: OfferSetListItem): void {
    const title = item?.data?.displayTitle || 'Disclosure';
    const content = item?.data?.fullDisclosure || '';

    this.disclosureTitle = title;
    this.disclosureHtml = this.sanitizer.bypassSecurityTrustHtml(content);
    this.disclosureDialogVisible = true;
  }

  /**
   * Closes the disclosure dialog
   */
  closeDisclosure(): void {
    this.disclosureDialogVisible = false;
    this.disclosureTitle = '';
    this.disclosureHtml = '';
  }

  /**
   * Opens delete confirmation for a single offer set
   */
  openDeleteConfirmation(item: OfferSetListItem): void {
    this.confirmationService.confirm({
      key: 'deleteOfferSet',
      defaultFocus: 'none',
      message: `Are you sure you want to delete "${item.data.displayTitle}"? Deleting Offer Sets may also remove associated rules, if present.`,
      header: 'Delete Offer Set',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger'
      },
      accept: () => {
        this.deleteOfferSet(item);
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  /**
   * Deletes a single offer set
   */
  deleteOfferSet(item: OfferSetListItem): void {
    const request: OfferSetBulkActionRequest = {
      accountId: this.accountId,
      action: 'delete',
      offerSetIds: [item.id]
    };
    this.offerSetListService.executeBulkAction(request).subscribe(
      () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Offer Set deleted.',
          life: 3000
        });
        this.loadOfferSets();
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete Offer Set.',
          life: 5000
        });
      }
    );
  }

  /**
   * Applies the selected bulk action to selected items
   */
  applyBulkAction(): void {
    let proceed = true;

    if (!this.selectedBulkAction) {
      proceed = false;
    }

    if (proceed && !this.selectAllAcrossPages && this.selectedItems.length === 0) {
      proceed = false;
    }

    if (proceed) {
      if (this.selectedBulkAction === 'delete') {
        this.openBulkDeleteConfirmation();
      } else if (this.selectAllAcrossPages) {
        this.executeBulkActionAcrossAll();
      } else {
        this.executeBulkAction();
      }
    }
  }

  /**
   * Opens bulk delete confirmation dialog
   */
  openBulkDeleteConfirmation(): void {
    this.confirmationService.confirm({
      key: 'bulkDeleteOfferSets',
      defaultFocus: 'none',
      message: `Are you sure you want to delete ${this.getSelectedCount()} Offer Set(s)? Deleting Offer Sets may also remove associated rules, if present.`,
      header: 'Delete Offer Sets',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger'
      },
      accept: () => {
        if (this.selectAllAcrossPages) {
          this.executeBulkActionAcrossAll();
        } else {
          this.executeBulkAction();
        }
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  /**
   * Executes the bulk action API call
   */
  executeBulkAction(): void {
    const selectedIds = this.selectedItems.map(item => item.id);
    const request: OfferSetBulkActionRequest = {
      accountId: this.accountId,
      action: this.selectedBulkAction,
      offerSetIds: selectedIds
    };
    this.offerSetListService.executeBulkAction(request).subscribe(
      (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `${response.successCount} Offer Set(s) updated successfully.`,
          life: 3000
        });
        if (response.failureCount > 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Warning',
            detail: `${response.failureCount} Offer Set(s) failed to update.`,
            life: 5000
          });
        }
        this.selectedBulkAction = '';
        this.clearSelection();
        this.loadOfferSets();
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to execute bulk action.',
          life: 5000
        });
      }
    );
  }

  /**
   * Executes bulk action when all results across pages are selected
   */
  private executeBulkActionAcrossAll(): void {
    this.isLoading = true;
    this.fetchAllIdsAcrossPages()
      .subscribe({
        next: (allIds) => {
          const request: OfferSetBulkActionRequest = {
            accountId: this.accountId,
            action: this.selectedBulkAction,
            offerSetIds: allIds
          };

          this.offerSetListService.executeBulkAction(request).subscribe(
            (response) => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `${response.successCount} Offer Set(s) updated successfully.`,
                life: 3000
              });
              if (response.failureCount > 0) {
                this.messageService.add({
                  severity: 'warn',
                  summary: 'Warning',
                  detail: `${response.failureCount} Offer Set(s) failed to update.`,
                  life: 5000
                });
              }
              this.selectedBulkAction = '';
              this.clearSelection();
              this.loadOfferSets();
            },
            () => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to execute bulk action.',
                life: 5000
              });
              this.isLoading = false;
            }
          );
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to gather all Offer Set IDs for bulk action.',
            life: 5000
          });
          this.isLoading = false;
        }
      });
  }

  /**
   * Returns the number of selected items, accounting for select all across pages
   */
  private getSelectedCount(): number {
    let count = this.selectedItems.length;

    if (this.selectAllAcrossPages) {
      let total = 0;

      if (this.pagination && typeof this.pagination.totalItems === 'number') {
        total = this.pagination.totalItems;
      }

      count = total;
    }

    return count;
  }

  /**
   * Collects all Offer Set IDs across all pages using current filters
   */
  private fetchAllIdsAcrossPages() {
    const pageSize = 100;
    const ids: number[] = [];

    const loadPage = (page: number, next: (done: boolean) => void) => {
      this.offerSetListService
        .getOfferSetList(
          this.accountId,
          this.activePreset,
          this.selectedStatus,
          this.selectedCondition,
          this.selectedVisibility,
          this.searchTerm,
          page,
          pageSize
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (resp) => {
            const pageIds = (resp.items || [])
              .filter((item) => !item.isShared)
              .map(i => i.id);
            ids.push(...pageIds);
            const totalPages = resp.pagination?.totalPages || 0;
            const done = page >= totalPages;
            next(done);
          },
          error: () => next(true)
        });
    };

    return new Observable<number[]>((subscriber) => {
      let current = 1;
      const iterate = () => {
        loadPage(current, (done) => {
          if (done) {
            subscriber.next(ids);
            subscriber.complete();
          } else {
            current += 1;
            iterate();
          }
        });
      };
      iterate();
    });
  }

  /**
   * Navigates to the inventory view
   */
  toggleInventoryMenu(event: MouseEvent, menu: Menu, button: HTMLButtonElement): void {
    const buttonWidth = `${button.offsetWidth}px`;
    menu.style = {width: buttonWidth, minWidth: buttonWidth};
    menu.toggle(event);
  }

  viewInventory(condition: 'new' | 'used'): void {
    const adminDomain = environment.launchcontrolAdminDomain;
    const accountId = this.accountId;

    if (!adminDomain || !accountId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Navigation Error',
        detail: 'Missing data required to open inventory.',
        life: 4000
      });
    } else {
      let specialsPath = 'new-specials';
      if (condition === 'used') {
        specialsPath = 'used-specials';
      }
      const url = `http://${adminDomain}/${specialsPath}/${accountId}#/specials/inventory`;

      window.location.href = url;
    }
  }

  /**
   * Opens the Build New Offer Set dialog and loads make/model options
   */
  buildNewOfferSet(): void {
    this.buildDialogVisible = true;
    this.loadVehicleOptions();
  }

  /**
   * Builds the year options for the offer-set condition.
   */
  private buildYearOptions(condition: string): number[] {
    const currentYear = new Date().getFullYear();
    const finalYear = currentYear + this.yearOptionsFutureOffset;
    let startYear = this.newVehicleStartYear;
    if (condition.toLowerCase() === this.stockConditionUsed) {
      startYear = this.usedVehicleStartYear;
    }

    return Array.from(
      {length: finalYear - startYear + 1},
      (_, index) => finalYear - index
    );
  }

  /**
   * Loads the makes and models used to populate the manual create sub-form
   */
  private loadVehicleOptions(): void {
    this.vehicleService.getAllMakes().pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (makes) => {
        this.makes = makes || [];
      },
      () => {
        this.makes = [];
      }
    );
    this.vehicleService.getAllModels().pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (models) => {
        this.models = models || [];
      },
      () => {
        this.models = [];
      }
    );
  }

  /**
   * Closes the Build dialog and opens the Manually Create Offer Set dialog
   */
  selectManualCreate(): void {
    this.buildDialogVisible = false;
    this.manualDialogVisible = true;
  }

  /**
   * Closes the Build dialog and opens the Enter from Stock dialog
   */
  selectEnterFromStock(): void {
    this.buildDialogVisible = false;
    this.stockDialogVisible = true;
  }

  /**
   * Returns from the Manually Create Offer Set dialog back to the Build dialog
   */
  backToBuildDialog(): void {
    this.manualDialogVisible = false;
    this.buildDialogVisible = true;
  }

  /**
   * Returns from the Enter from Stock dialog back to the Build dialog
   */
  backToBuildDialogFromStock(): void {
    this.stockDialogVisible = false;
    this.buildDialogVisible = true;
  }

  /**
   * Filters the model list to the selected make and resets the model selection
   */
  onBuildMakeChange(): void {
    const selectedMakeId = this.newOfferSetMakeId;
    this.filteredModels = this.models.filter((model) => model.makeId === selectedMakeId);
    this.newOfferSetModelId = null;
  }

  /**
   * Closes the Build New Offer Set dialog and clears the form state
   */
  closeBuildDialog(): void {
    this.buildDialogVisible = false;
    this.resetBuildForm();
  }

  /**
   * Closes the Manually Create Offer Set dialog and clears the form state
   */
  closeManualDialog(): void {
    this.manualDialogVisible = false;
    this.resetBuildForm();
  }

  /**
   * Closes the Enter from Stock dialog and clears the form state
   */
  closeStockDialog(): void {
    this.stockDialogVisible = false;
    this.resetBuildForm();
  }

  /**
   * Resets the manual create sub-form to its default state
   */
  private resetBuildForm(): void {
    this.newOfferSetCondition = this.conditionNew;
    this.newOfferSetYear = null;
    this.newOfferSetMakeId = null;
    this.newOfferSetModelId = null;
    this.newOfferSetTrim = '';
    this.newOfferSetStockNumber = '';
    this.filteredModels = [];
  }

  /**
   * Validates and submits stock creation once per user action.
   */
  enterFromStock(): void {
    if (!this.isCreatingFromStock) {
      const request = this.buildFromStockRequest();

      if (!request) {
        this.messageService.add({
          severity: 'error',
          summary: 'Create Error',
          detail: 'Missing data required to create the Offer Set from stock.',
          life: 4000
        });
      } else {
        this.submitCreateAdFromStock(request);
      }
    }
  }

  /**
   * Reproduces the legacy close, notification, and request sequence.
   */
  private submitCreateAdFromStock(request: CreateAdFromStockRequest): void {
    this.isCreatingFromStock = true;
    this.stockDialogVisible = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Creating Offer Set',
      detail: 'Creating new offer set from stock number...',
      life: 4000
    });
    this.offerSetListService.createAdFromStock(
      request.accountId,
      request.stockNumber,
      request.expectedCondition
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (response) => {
        this.handleCreateAdFromStockResponse(response, request.stockNumber);
      },
      () => {
        this.isCreatingFromStock = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Create Error',
          detail: 'Unable to create offer set from stock number. Please contact support@dealerteamwork.com.',
          life: 4000
        });
      }
    );
  }

  /**
   * Builds the from-stock request from the modal input, or null when required data is missing
   */
  private buildFromStockRequest(): CreateAdFromStockRequest | null {
    const accountId = this.accountId;
    const stockNumber = (this.newOfferSetStockNumber || '').trim();
    const expectedCondition = this.resolveExpectedStockCondition();
    let request: CreateAdFromStockRequest | null = null;

    if (accountId && stockNumber && this.isAllowedStockCondition(expectedCondition)) {
      request = {
        accountId: accountId,
        stockNumber: stockNumber,
        expectedCondition: expectedCondition
      };
    }

    return request;
  }

  /**
   * Preserves the legacy endpoint contract without requiring another modal input.
   * The legacy UI inferred this value from its New/Used page context. The combined
   * list uses the active condition when available and New as the fallback.
   */
  private resolveExpectedStockCondition(): string {
    let expectedCondition = this.stockConditionNew;
    if (this.isAllowedStockCondition(this.selectedCondition)) {
      expectedCondition = this.selectedCondition;
    }

    return expectedCondition;
  }

  /**
   * Separates business errors in the HTTP-200 envelope from successful creation.
   */
  private handleCreateAdFromStockResponse(response: CreateAdFromStockResponse, stockNumber: string): void {
    this.isCreatingFromStock = false;
    if (!response.success || !response.result) {
      this.showCreateAdFromStockErrors(response.errors);
    } else {
      const editorCondition = this.resolveEditorCondition(response.result.newUsed);
      if (!editorCondition) {
        this.showCreateAdFromStockErrors(['Created offer set has an unsupported vehicle condition.']);
      } else if (response.result.expectedConditionMatches) {
        this.redirectToStockAdEditor(response.result.adId, editorCondition);
      } else {
        this.handleExpectedConditionMismatch(response.result.adId, stockNumber, editorCondition);
      }
    }
  }

  /**
   * Displays every business error returned by the existing endpoint.
   */
  private showCreateAdFromStockErrors(errors: string[]): void {
    let errorMessages = errors;
    if (errorMessages.length === 0) {
      errorMessages = ['Failed to create the Offer Set from stock.'];
    }
    errorMessages.forEach((detail) => {
      this.messageService.add({
        severity: 'error',
        summary: 'Create Error',
        detail: detail,
        life: 4000
      });
    });
  }

  /**
   * Validates the expected condition against the new/used allow-list
   */
  private isAllowedStockCondition(condition: string): boolean {
    const allowedConditions = [this.stockConditionNew, this.stockConditionUsed];

    return allowedConditions.includes(condition);
  }

  /**
   * Maps the inventory newUsed value to the condition used for the editor redirect
   */
  private resolveEditorCondition(newUsed: string): string | null {
    const normalized = (newUsed || '').trim().toLowerCase();
    const newValues = ['n', this.stockConditionNew];
    const usedValues = ['u', this.stockConditionUsed];
    let condition: string | null = null;

    if (newValues.includes(normalized)) {
      condition = this.stockConditionNew;
    }
    if (usedValues.includes(normalized)) {
      condition = this.stockConditionUsed;
    }

    return condition;
  }

  /**
   * Notifies the user that the vehicle was created under the opposite condition, then redirects after a delay
   */
  private handleExpectedConditionMismatch(adId: number, stockNumber: string, actualCondition: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Vehicle Condition Updated',
      detail: `The vehicle associated with stock number ${stockNumber} was created with the "${actualCondition}" vehicle condition. Redirecting to the editor…`,
      life: this.mismatchRedirectDelayMs
    });
    setTimeout(() => {
      this.redirectToStockAdEditor(adId, actualCondition);
    }, this.mismatchRedirectDelayMs);
  }

  /**
   * Redirects a stock-created legacy ad through its persisted condition route.
   */
  private redirectToStockAdEditor(adId: number, condition: string): void {
    const accountId = this.accountId;
    if (!accountId || !adId || !this.isAllowedStockCondition(condition)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Navigation Error',
        detail: 'Missing data required to open the stock-created Offer Set.',
        life: 4000
      });
    } else {
      const url = `${window.location.origin}/${condition}-specials/${accountId}#/specials/edit/${adId}`;

      window.location.href = url;
    }
  }

  /**
   * Submits the manual create form to the create endpoint and redirects on success
   */
  manuallyCreateOfferSet(): void {
    const request = this.buildCreateRequest();

    if (!request) {
      this.messageService.add({
        severity: 'error',
        summary: 'Navigation Error',
        detail: 'Missing data required to create the Offer Set.',
        life: 4000
      });
    } else {
      this.submitCreateOfferSet(request);
    }
  }

  /**
   * Sends the validated manual create request and redirects on success
   */
  private submitCreateOfferSet(request: OfferSetCreateRequest): void {
    const condition = request.vehicle.condition;
    this.offerSetListService.createOfferSet(request).pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (response) => {
        this.manualDialogVisible = false;
        this.redirectToOfferSetEditor(response.id, condition);
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Create Error',
          detail: 'Failed to create the Offer Set.',
          life: 4000
        });
      }
    );
  }

  /**
   * Builds the create request from the manual form, or null when required data is missing
   */
  private buildCreateRequest(): OfferSetCreateRequest | null {
    const accountId = this.accountId;
    const condition = this.newOfferSetCondition;
    const year = this.newOfferSetYear;
    const selectedMake = this.makes.find((make) => make.id === this.newOfferSetMakeId) ?? null;
    const selectedModel = this.models.find((model) => model.id === this.newOfferSetModelId) ?? null;
    let request: OfferSetCreateRequest | null = null;

    if (accountId && condition && year && selectedMake && selectedModel) {
      const trim = this.newOfferSetTrim || null;
      request = {
        accountId: accountId,
        vehicle: {
          year: year,
          make: selectedMake.displayName,
          model: selectedModel.name,
          modelId: selectedModel.id,
          trim: trim,
          condition: condition
        }
      };
    }

    return request;
  }

  /**
   * Redirects to the legacy editor for the newly created offer set
   */
  private redirectToOfferSetEditor(offerSetId: number, condition: string): void {
    const adminDomain = environment.launchcontrolAdminDomain;
    const accountId = this.accountId;

    if (!adminDomain || !accountId || !offerSetId || !condition) {
      this.messageService.add({
        severity: 'error',
        summary: 'Navigation Error',
        detail: 'Missing data required to open the editor.',
        life: 4000
      });
    } else {
      const url = `http://${adminDomain}/new-specials/${accountId}#/specials/edit/${offerSetId}`;

      window.location.href = url;
    }
  }
}
